import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  updatePassword,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  type User
} from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { requestNotificationPermission, initializeFCM } from '../utils/fcmService';
import LoadingSpinner from '../components/LoadingSpinner';
import { 
  getDocumentWithRetry, 
  setDocumentWithRetry,
  getDocRef 
} from '../utils/firestoreOperations';
import { getFirebaseErrorMessage } from '../utils/offlineHandler';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signup: (email: string, password: string, fullName: string, termsAccepted: boolean) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (fullName: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  isEmailVerified: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  console.log('🔐 [AuthProvider] Initializing... loading=', loading, 'user=', user?.email || 'none');

  useEffect(() => {
    console.log('🔐 [AuthProvider] useEffect fired - setting up auth listener');
    // Initialize FCM on app load
    initializeFCM();
    
    // Use a local variable to track component mount state
    let isSubscribed = true;

    const unsubscribe = onAuthStateChanged(auth, 
      async (user) => {
        if (!isSubscribed) return;

        try {
          if (user) {
            // Use retry-enabled function for Firestore operations
            try {
              const userDocRef = getDocRef(db, 'users', user.uid);
              const userDoc = await getDocumentWithRetry(userDocRef);
              
              if (!userDoc.exists()) {
                await setDocumentWithRetry(userDocRef, {
                  uid: user.uid,
                  email: user.email,
                  fullName: user.displayName || '',
                  phoneNumber: user.phoneNumber || '',
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  isEmailVerified: user.emailVerified,
                  lastLogin: new Date().toISOString(),
                  role: 'user',
                  addresses: []
                });
              }
            } catch (firestoreError) {
              // If Firestore fails (offline), still set the user
              console.warn('⚠ Firestore unavailable, user data will sync when online');
              // Don't throw - allow user to continue
            }
            
            // Request notification permission after successful login
            // Wait a bit to not overwhelm the user immediately
            setTimeout(async () => {
              try {
                await requestNotificationPermission(user.uid);
              } catch (fcmError) {
                console.warn('⚠ Push notifications unavailable:', fcmError instanceof Error ? fcmError.message : 'Unknown error');
                // Don't block login if FCM fails
              }
            }, 2000);
          }
          setUser(user);
        } catch (err) {
          const errorMessage = getFirebaseErrorMessage(err);
          console.error('❌ Auth state change error:', errorMessage);
          if (isSubscribed) {
            setError(errorMessage);
          }
        } finally {
          if (isSubscribed) {
            setLoading(false);
          }
        }
      },
      (error) => {
        const errorMessage = getFirebaseErrorMessage(error);
        console.error('❌ Auth state error:', errorMessage);
        if (isSubscribed) {
          setError(errorMessage);
          setLoading(false);
        }
      }
    );

    return () => {
      isSubscribed = false;
      unsubscribe();
    };
  }, []);

  const signup = async (email: string, password: string, fullName: string, termsAccepted: boolean) => {
    try {
      const { user: newUser } = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update the user's display name in Firebase Auth
      await updateProfile(newUser, { displayName: fullName });
      
      // Create/update the user document with the full name and terms acceptance
      const userDocRef = doc(db, 'users', newUser.uid);
      await setDoc(userDocRef, {
        uid: newUser.uid,
        email: newUser.email,
        fullName: fullName,
        phoneNumber: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isEmailVerified: false,
        lastLogin: new Date().toISOString(),
        role: 'user',
        addresses: [],
        termsAccepted: termsAccepted,
        termsAcceptedAt: termsAccepted ? new Date().toISOString() : null
      });
      
      await sendEmailVerification(newUser);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during signup');
      throw err;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during login');
      throw err;
    }
  };

  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Check if user document exists, create if not
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        // Create complete user profile for first-time Google sign-in
        // This is equivalent to signing up with Google
        const newUserProfile = {
          uid: user.uid,
          email: user.email || '',
          fullName: user.displayName || '',
          displayName: user.displayName || '',
          phoneNumber: user.phoneNumber || '',
          isPhoneVerified: false,
          isEmailVerified: user.emailVerified, // Google accounts are pre-verified
          profileImage: user.photoURL || '',
          addresses: [],
          defaultAddressId: undefined,
          role: 'user',
          totalOrders: 0,
          totalSpent: 0,
          averageRating: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          lastLogin: new Date().toISOString()
        };
        
        await setDoc(userDocRef, newUserProfile);
        console.log('[Auth] Created new user profile for Google sign-in:', user.uid);
      } else {
        // Update last login time and email verification status for existing users
        await setDoc(userDocRef, {
          lastLogin: new Date().toISOString(),
          isEmailVerified: user.emailVerified,
          updatedAt: new Date().toISOString(),
          // Update display name and photo if changed in Google account
          ...(user.displayName && { displayName: user.displayName }),
          ...(user.photoURL && { profileImage: user.photoURL })
        }, { merge: true });
        console.log('[Auth] Updated existing user login info:', user.uid);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during Google sign-in');
      throw err;
    }
  };

  const logout = async () => {
    try {
      // Note: Cart is already saved to Firestore automatically via CartContext
      // We only clear localStorage to prevent cross-user data leaks
      // The cart will be restored from Firestore when user logs back in
      localStorage.removeItem('cart');
      console.log('[Auth] Cleared cart from localStorage on logout (cart preserved in Firestore)');
      
      await signOut(auth);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during logout');
      throw err;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    signup,
    login,
    loginWithGoogle,
    logout,
    updateProfile: async (fullName: string) => {
      if (!user) throw new Error('No user logged in');
      await updateProfile(user, { displayName: fullName });
      
      // Update Firestore document
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        fullName,
        updatedAt: new Date().toISOString()
      }, { merge: true });
    },
    updatePassword: async (newPassword: string) => {
      if (!user) throw new Error('No user logged in');
      await updatePassword(user, newPassword);
    },
    sendPasswordResetEmail: async (email: string) => {
      await sendPasswordResetEmail(auth, email);
    },
    isEmailVerified: user?.emailVerified ?? false
  };

  // Show loading spinner while initializing auth state
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
